import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  PLATFORM_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import {
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { MatTooltipModule } from '@angular/material/tooltip';
import { serverErrorInterceptor, tokenInterceptor } from '@core/interceptors';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideState, provideStore, Store } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { authReducer } from '@core/store/auth/auth.reducer';
import { globalReducer } from '@core/store/global/global.reducer';
import { GlobalEffects } from '@core/store/global/global.effect';
import { AuthActions } from '@core/store/auth/auth.actions';
import { LocalStorageService } from '@core/services/local-storage.service';
import { APP_BASE_HREF, isPlatformBrowser } from '@angular/common';
import { requestTimeoutHttpInterceptor } from './core/interceptors/timeout.interceptor';
import { LOCAL_STORAGE_KEY_AUTH_TOKEN, LOCAL_STORAGE_KEY_USER_NAME } from './core/constant/constant';
import { AuthEffects } from '@core/store/auth/auth.effect';
import { isTokenValid } from '@core/utils/auth';
import { AuthToken } from '@core/interface/common/auth-token';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '@env/environment';
import { isExternalPricingLocation } from '@core/utils/external-pricing-session';
import { applyPricingLanguage } from '@core/utils/pricing-language.util';

export function createTranslateLoader(
  http: HttpClient
): TranslateHttpLoader {
  return new TranslateHttpLoader(
    http,
    './assets/i18n/',
    '.json'
  );
}

export function appInitializerFactory(
  translate: TranslateService,
  localStorageService: LocalStorageService,
  platformId: Object
) {
  return () => {
    return new Promise<void>((resolve) => {
      const isExternalPricing =
        isPlatformBrowser(platformId) && isExternalPricingLocation();

      if (isExternalPricing) {
        applyPricingLanguage(translate, localStorageService);
      } else {
        translate.setDefaultLang('en');
        translate.use('en');
      }

      translate.use(translate.currentLang).subscribe({
        next: () => {
          console.log('Successfully initialized translations.');
        },
        error: (err) => {
          console.error('Error initializing translations:', err);
          resolve();
        },
        complete: () => {
          resolve();
        },
      });
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withFetch()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideClientHydration(),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        serverErrorInterceptor,
        requestTimeoutHttpInterceptor,
        tokenInterceptor,
      ])
    ),
    importProvidersFrom(MatTooltipModule),
    provideAnimationsAsync(),
    provideStore(),
    provideEffects([GlobalEffects, AuthEffects]),
    provideState({ name: 'authState', reducer: authReducer }),
    provideState({ name: 'global', reducer: globalReducer }),
    {
      provide: APP_INITIALIZER,
      useFactory:
        (
          store: Store,
          localStorageService: LocalStorageService,
          platformId: Object
        ) =>
          () => {
            return new Promise<void>((resolve) => {
              if (isPlatformBrowser(platformId)) {
                if (isExternalPricingLocation()) {
                  resolve();
                  return;
                }
                const data = localStorageService.getData(LOCAL_STORAGE_KEY_AUTH_TOKEN);
                const userName = localStorageService.getData(LOCAL_STORAGE_KEY_USER_NAME);
                if (data) {
                  try {
                    const token: AuthToken = JSON.parse(data);
                    if (isTokenValid(token)) {
                      store.dispatch(
                        AuthActions.loginSuccess(userName, token)
                      );
                    } else if (token.refreshToken) {
                      store.dispatch(AuthActions.refreshToken());
                    } else {
                      localStorageService.removeData(LOCAL_STORAGE_KEY_AUTH_TOKEN);
                      localStorageService.removeData(LOCAL_STORAGE_KEY_USER_NAME);
                    }
                  } catch (e) {
                    console.error('Invalid token data in localStorage:', e);
                    localStorageService.removeData(LOCAL_STORAGE_KEY_AUTH_TOKEN);
                    localStorageService.removeData(LOCAL_STORAGE_KEY_USER_NAME);
                  }
                }
              }
              resolve();
            });
          },
      deps: [Store, LocalStorageService, PLATFORM_ID],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, LocalStorageService, PLATFORM_ID],
      multi: true,
    },
    { provide: APP_BASE_HREF, useValue: environment.baseHref },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (createTranslateLoader),
          deps: [HttpClient]
        },
        useDefaultLang: true,
        // defaultLanguage: 'en',
        isolate: true,
      })
    ),
  ],
};
